import { useState } from "react";
import { X, Sparkles, Mail, Loader2, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import ShareSection from "./ShareSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComingSoonModal({ isOpen, onClose }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/early-access`, {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim() || null
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("You're on the list!");
        
        // Track TikTok conversion event
        if (window.ttq) {
          window.ttq.track('CompleteRegistration', {
            content_name: 'Early Access Signup',
            status: 'success'
          });
        }
      }
    } catch (error) {
      console.error("Early access error:", error);
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="coming-soon-modal"
    >
      <div 
        className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl animate-fadeIn my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-6 sm:p-8 text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#4FB3A5] to-[#3d9a8d] flex items-center justify-center mx-auto mb-6 shadow-lg">
            {isSubmitted ? (
              <PartyPopper className="text-white" size={32} />
            ) : (
              <Sparkles className="text-white" size={32} />
            )}
          </div>

          {isSubmitted ? (
            <>
              {/* Success State */}
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F3A5F] mb-4">
                You're on the early access list 👍
              </h2>
              <div className="bg-gradient-to-br from-[#EAF6F4] to-[#F7F9FB] rounded-xl p-4 mb-6 border border-[#4FB3A5]/20">
                <p className="text-sm text-[#1F3A5F] font-medium mb-1">
                  You've locked in founder pricing:
                </p>
                <p className="text-[#4FB3A5] font-bold text-lg">
                  Pro for $5.99 for the entire first year when we launch.
                </p>
              </div>
              
              {/* Share Section */}
              <div className="mb-6">
                <ShareSection />
              </div>
              
              <Button
                onClick={onClose}
                className="w-full py-5 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-medium"
              >
                Continue to App
              </Button>
            </>
          ) : (
            <>
              {/* Form State */}
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F3A5F] mb-3">
                Get premium features + updates at launch
              </h2>

              <p className="text-[#6B7280] leading-relaxed mb-6">
                Join early access and lock in Pro for <span className="font-semibold text-[#4FB3A5]">$5.99</span> for the entire first year when we launch.
              </p>

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="First name (optional)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-4 rounded-xl border-gray-200 focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20"
                    data-testid="early-access-name-input"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-4 rounded-xl border-gray-200 focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20"
                    data-testid="early-access-email-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 rounded-xl bg-[#4FB3A5] hover:bg-[#3d9a8d] text-white font-medium"
                  data-testid="early-access-submit-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    "Join Early Access"
                  )}
                </Button>
              </form>

              {/* Compact Share Section */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <ShareSection compact />
              </div>

              {/* Footer */}
              <p className="mt-4 text-xs text-[#A0A7B4]">
                No spam. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
